import type React from 'react';
import {useState} from 'react';
import {Alert, Box, Skeleton, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography} from '@mui/material';
import {SegmentedControl} from '~/components/segmented-control/SegmentedControl';
import {perUnit} from '../../domain/unitPrice';
import {useBenchmarkDetailQuery} from '../../hooks/useCategoryProfileQueries';
import type {BenchmarkDetail} from '../../types/benchmarkTypes';
import {LOCALE} from '~/pages/category-profile/domain/locale';

const VIEWS = [
    {label: 'Sites', key: 'sites', heading: 'Site'},
    {label: 'Business units', key: 'businessUnits', heading: 'Business unit'},
    {label: 'Suppliers', key: 'suppliers', heading: 'Supplier'},
] as const;

type Props = {
    databagId: number;
    path: string;
    id: number;
    unit: string;
    money: (v: number) => string;
    price: (v: number | null) => string;
    /** The dataset has no site column: business units were compared, so there is no Sites view. */
    withoutSites?: boolean;
};

/** The expanded row of an item group: price paid per site (or business unit, supplier), gap to best, saving. */
export const BenchmarkSitesPanel: React.FC<Props> = ({databagId, path, id, unit, money, price, withoutSites = false}) => {
    const {data, isLoading, isError} = useBenchmarkDetailQuery(databagId, path, id);
    const [view, setView] = useState(0);
    if (isError) return <Alert severity="error" sx={{m: 1}}>Could not load the sites.</Alert>;
    if (isLoading || !data) return <Skeleton variant="rounded" height={120} sx={{m: 1}}/>;

    const views = withoutSites ? VIEWS.filter(v => v.key !== 'sites') : VIEWS;
    const {key, heading} = views[Math.min(view, views.length - 1)];
    const rows = (data as BenchmarkDetail)[key];
    return (
        <Box sx={{px: 6, py: 1.5, bgcolor: 'action.hover'}}>
            <Stack direction="row" alignItems="center" gap={2} sx={{mb: 1}}>
                <SegmentedControl labels={views.map(v => v.label)} value={view} onChange={setView}/>
                <Typography variant="caption" color="text.secondary">
                    {`${data.group.lines} transactions compared${data.group.excludedLines ? `, ${data.group.excludedLines} left out (no quantity, other unit or unit error)` : ''}`}
                </Typography>
            </Stack>
            <Table size="small" aria-label={`${heading} prices`}>
                <TableHead>
                    <TableRow>
                        <TableCell>{heading}</TableCell>
                        <TableCell align="right">Price paid</TableCell>
                        <TableCell align="right">vs best</TableCell>
                        <TableCell align="right">Bought</TableCell>
                        <TableCell align="right">Spend</TableCell>
                        {key === 'sites' && <TableCell align="right">Saving at best price</TableCell>}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map(r => (
                        <TableRow key={r.key}>
                            <TableCell>{r.key}</TableCell>
                            <TableCell align="right">{perUnit(price(r.price), unit)}</TableCell>
                            <TableCell align="right" sx={{color: (r.vsBestPct ?? 0) > 0 ? 'warning.dark' : 'success.main'}}>
                                {r.vsBestPct === null ? '—' : r.vsBestPct > 0 ? `+${r.vsBestPct.toFixed(1)}%` : 'Best'}
                            </TableCell>
                            <TableCell align="right">{`${r.quantity.toLocaleString(LOCALE, {maximumFractionDigits: 1})} ${unit}`}</TableCell>
                            <TableCell align="right">{money(r.spend)}</TableCell>
                            {key === 'sites' && <TableCell align="right">{r.saving ? money(r.saving) : '—'}</TableCell>}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </Box>
    );
};
